import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { CSSProperties, VFC } from "react";
import styled from "styled-components";
import { LegacyImage } from "components";
import { useAnchor } from "hooks";
import { getImageFormat } from "lib/utils";
import { Stats3Content } from "types/graphql/generated";

interface Props {
  content: Stats3Content;
}

const Stats3: VFC<Props> = withErrorBoundary(({ content: { stats3Title, stats3Investors } }) => {
  useAnchor("stats3");

  return (
    <StyledStats3 className="stats3 Content">
      <h2 className="Stats3__title Font_40_48_600">{stats3Title}</h2>
      <ul className="Stats3__cards">
        {stats3Investors.map(
          ({
            id,
            stats3InvestorsLogo: {
              data: { stats3InvestorsLogo },
            },
          }) => (
            <li
              key={id}
              className="Stats3__card"
              style={
                {
                  "--logo-aspect-ratio": stats3InvestorsLogo.width / stats3InvestorsLogo.height,
                  "--logo-height": `${stats3InvestorsLogo.height}px`,
                } as CSSProperties
              }
            >
              <LegacyImage
                src={stats3InvestorsLogo.src}
                alt={stats3InvestorsLogo.alt}
                layout="fill"
                objectFit="contain"
                transform={{ format: getImageFormat(stats3InvestorsLogo.ext) }}
                className="Stats3__cardIcon"
              />
            </li>
          )
        )}
      </ul>
    </StyledStats3>
  );
}, {});

const StyledStats3 = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Stats3__cards {
    display: flex;
    align-items: center;
    margin-top: 40px;
  }

  .Stats3__card {
    position: relative;
    max-height: 46px;
    height: var(--logo-height);
    width: calc(var(--logo-aspect-ratio) * min(46px, var(--logo-height)));
  }

  .Stats3__card:not(:first-child) {
    margin-left: 150px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    display: flex;
    justify-content: space-between;

    .Stats3__cards {
      margin-top: 0;
      display: grid;
      grid-template-columns: repeat(2, 214px);
      gap: 40px 32px;
    }

    .Stats3__card:not(:first-child) {
      margin-left: 0;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    flex-direction: column;

    .Stats3__cards {
      grid-template-columns: repeat(1, auto);
      gap: 40px 0;
      margin-top: 50px;
    }
  }
`;

gql`
  fragment Stats3Content on ComponentStatsStats3 {
    stats3Title
    stats3Investors {
      id
      stats3InvestorsLogo {
        data {
          stats3InvestorsLogo: attributes {
            src: hash
            alt: alternativeText
            width: width
            height: height
            ext
          }
        }
      }
    }
  }
`;

export { Stats3 };
export type { Props as Stats3Props };
